import { useCallback, useEffect, useMemo, useState } from 'react';
import { CATEGORIES, PRODUCTS, REVIEWS } from './products';

const BLANK = { code: '', products: [], reviews: [], overrides: {}, removed: [], removedReviews: [], categories: [] };

let snapshot = null;
let pending = null;
const listeners = new Set();

function fetchCatalog() {
  if (pending) return pending;
  pending = fetch('/api/catalog', { credentials: 'same-origin' })
    .then((response) => response.json())
    .then((data) => {
      snapshot = { ...BLANK, ...data, code: data.code ?? '' };
      return snapshot;
    })
    .catch(() => {
      snapshot = snapshot ?? BLANK;
      return snapshot;
    })
    .finally(() => {
      pending = null;
      listeners.forEach((update) => update(snapshot));
    });
  return pending;
}

export function useCatalog() {
  const [saved, setSaved] = useState(snapshot ?? BLANK);

  useEffect(() => {
    listeners.add(setSaved);
    if (snapshot === null) fetchCatalog();
    else setSaved(snapshot);
    return () => {
      listeners.delete(setSaved);
    };
  }, []);

  const reload = useCallback(() => {
    fetchCatalog();
  }, []);

  const products = useMemo(() => {
    const gone = new Set(saved.removed);
    const starter = PRODUCTS.filter((product) => !gone.has(product.id)).map((product) =>
      saved.overrides[product.id] ? { ...product, ...saved.overrides[product.id] } : product
    );
    return [...starter, ...saved.products.filter((product) => !gone.has(product.id))];
  }, [saved]);

  const reviews = useMemo(() => {
    const removed = new Set(saved.removedReviews);
    return [...REVIEWS.filter((review) => !removed.has(review.id)), ...saved.reviews];
  }, [saved]);

  const categories = useMemo(() => {
    const starter = new Set(CATEGORIES.map((category) => category.id));
    const added = (saved.categories ?? []).filter(
      (category) => category?.id && category?.title && !starter.has(category.id)
    );
    return [...CATEGORIES, ...added];
  }, [saved]);

  return { products, reviews, categories, reload, ready: snapshot !== null, load: { code: saved.code } };
}
